import React, { useMemo, useState } from 'react';
import { stylesheet } from 'typestyle';
import { ADALONG_COLORS } from '@src/styles';
import { CONTENT_SIZE_DEFAULT, MIN_SIZE_VALUE, MAX_SIZE_VALUE } from '../utils/variables';

interface ContentSizeSliderProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}

export function ContentSizeSlider({
  value = CONTENT_SIZE_DEFAULT,
  onChange,
  min = MIN_SIZE_VALUE,
  max = 10, // Limiting the max for better UI experience in slider
}: ContentSizeSliderProps) {
  const displayValues = [1, 3, 5, 10]
  const [hoveredDot, setHoveredDot] = useState<number | null>(null);
 
  const displayedValues = useMemo(() => {
    const filteredValues = displayValues.filter(val => val >= min && val <= max);
    
    if (!filteredValues.includes(min)) {
      filteredValues.unshift(min);
    }
    if (!filteredValues.includes(max) && max <= 10) {
      filteredValues.push(max);
    }
    
    return filteredValues.sort((a, b) => a - b);
  }, [min, max, displayValues]);
  
  const allAvailableValues = useMemo(
    () => Array.from({ length: max - min + 1 }, (_, index) => min + index),
    [min, max]
  );

  // Calculate the progress percentage for the blue track
  const progressPercentage = useMemo(() => {
    return ((value - min) / (max - min)) * 100;
  }, [value, min, max]);
  
  // Calculate positions for all dots as percentages
  const getPositionPercentage = (val: number) => {
    return ((val - min) / (max - min)) * 100;
  };

  const handleClick = (selectedValue: number) => {
    onChange(selectedValue);
  };
  
  // Handle click on the track to select the closest value
  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const trackRect = e.currentTarget.getBoundingClientRect();
    const clickPosition = e.clientX - trackRect.left;
    const clickPercentage = (clickPosition / trackRect.width) * 100;
    
    // Find the closest value to where the user clicked
    // Prefer the displayed values if click is close to them
    const preferenceForDisplayed = 10; 
    
    let closestValue = allAvailableValues[0];
    let minDistance = Math.abs(getPositionPercentage(closestValue) - clickPercentage);
    
    allAvailableValues.forEach(val => {
      const distance = Math.abs(getPositionPercentage(val) - clickPercentage);
      // Give preference to displayed values
      const adjustedDistance = displayedValues.includes(val) ? 
        distance - preferenceForDisplayed : distance;
      
      if (adjustedDistance < minDistance) {
        minDistance = adjustedDistance;
        closestValue = val;
      }
    });
    
    onChange(closestValue);
  };

  const trackStyle = {
    '--progress': `${progressPercentage}%`,
  } as React.CSSProperties;

  return (
    <div className={styles.container}>
      <div className={styles.track} style={trackStyle} onClick={handleTrackClick}>
        <div className={styles.progressTrack} />
        
        {/* Render dots for all possible values to make clicking easier */}
        {allAvailableValues.map((sliderValue) => {
          const showLabel = displayedValues.includes(sliderValue);
          
          const positionStyle = {
            left: `${getPositionPercentage(sliderValue)}%`,
          };
          
          // Determine if we should show the label (either it's a display value or it's being hovered)
          const shouldShowLabel = showLabel || hoveredDot === sliderValue;
          
          return (
            <div
              key={sliderValue}
              className={styles.dotWrapper}
              style={positionStyle}
              onClick={(e) => {
                e.stopPropagation();
                handleClick(sliderValue);
              }}
              onMouseEnter={() => setHoveredDot(sliderValue)}
              onMouseLeave={() => setHoveredDot(null)}
            >
              <div
                className={`${styles.dot} ${
                  value === sliderValue ? styles.dotSelected : ''
                } ${sliderValue <= value ? styles.dotActive : ''}`}
              />
              {shouldShowLabel ? (
                <div
                  className={`${styles.label} ${
                    value === sliderValue ? styles.labelSelected : ''
                  } ${hoveredDot === sliderValue && !showLabel ? styles.labelHovered : ''}`}
                >
                  {sliderValue}
                </div>
              ) : null }
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = stylesheet({
  container: {
    position: 'relative',
    width: '100%',
    padding: '8px 0 30px', 
    userSelect: 'none',
    maxWidth: '350px',
  },
  track: {
    position: 'relative',
    width: '100%',
    height: '4px',
    backgroundColor: '#333',
    cursor: 'pointer',
  },
  progressTrack: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: 'var(--progress)',
    backgroundColor: ADALONG_COLORS.FLASH_BLUE,
    transition: 'width 0.3s ease',
    pointerEvents: 'none', 
  },
  dotWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'absolute',
    transform: 'translateX(-50%)', // Center the dot on its position
    zIndex: 2,
    padding: '10px',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#4a4a4a',
    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    position: 'relative', 
    top: '-12px',
  },
  dotActive: {
    backgroundColor: ADALONG_COLORS.FLASH_BLUE, 
  },
  dotSelected: {
    backgroundColor: ADALONG_COLORS.FLASH_BLUE,
    transform: 'scale(1.2)',
    top: '-12px',
  },
  label: {
    fontSize: '0.8em',
    color: '#aaa',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    textAlign: 'center',
  },
  labelSelected: {
    fontWeight: 'bold',
  },
  labelHovered: {
    opacity: 0.8,
  },
});
